import type { StationeryDraftFormat, StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import type { NavigationStep } from './CardCustomizer.types';

const CATEGORY = 'messaging.card';
const PAGE_PREFIX = 'admin';
const PAGE = 'customize';

export interface CustomizePageArgs {
  category: StationeryTemplateCategoryEnum;
  themeId: string;
  step: 'cardFront' | 'cardBack' | 'envelope' | 'recipients' | 'postcardRecipients' | 'review' | 'postCheckout';
}

export interface CustomizationButtonInteractedArgs {
  property: string;
  value?: string | number | boolean;
}

/**
 * Every Segment event name emitted through the generic `track` action.
 *
 * These strings are a live dashboard contract — renaming one silently breaks existing queries,
 * so coordinate with the owning PM before changing an entry. Add a new member here when adding
 * a one-off event; use `findReferences` on the member to locate its emitters.
 */
export type CardCustomizerTrackedAction =
  | 'CardLayerFocused'
  | 'FormFieldInteracted'
  | 'ImageFormatChecked'
  | 'ResolutionWarningShown'
  | 'SavedPromoCodeInvalidated'
  | 'ShippingAddressAutoPopulated'
  | 'SmartyApiSuccess';

/**
 * Raw `action` values emitted through the generic `trackError` action.
 *
 * `actionType: 'error'` makes `trackFunc` **append `Error` to the event name**, so these are not
 * the names that reach Segment: `'SmartyApi'` arrives as `SmartyApiError`, and `'loadFontError'`
 * arrives as `loadFontErrorError` (a pre-existing quirk of that event, preserved deliberately).
 * Same dashboard-contract caveat as {@link CardCustomizerTrackedAction} applies.
 */
export type CardCustomizerTrackedErrorAction = 'loadFontError' | 'MessagingStripeRedirectTimeout' | 'PhotoSelectionError' | 'SendPremiumDigitalCard' | 'SmartyApi';

/**
 * Every `name` emitted through the generic `buttonInteracted` action.
 *
 * All of these fire the shared `MessagingButtonInteracted` event, so `name` — not `action` — is
 * what identifies them in Amplitude. Same dashboard-contract caveat as
 * {@link CardCustomizerTrackedAction} applies; use `findReferences` on a member to find emitters.
 *
 * Note: this is deliberately tighter than the sibling `Card.telemetry.ts` `buttonInteracted`,
 * which takes a free-form payload.
 */
export type CardCustomizerButtonName =
  | 'digitalEnvelopeCustomizationCompleted'
  | 'exitCustomizationWarning_cancel'
  | 'exitCustomizationWarning_openCta'
  | 'exitCustomizationWarning_viewDesignsCta'
  | 'exitCustomizationWarning_viewDraftsCta'
  | 'promoCodeInput_applyCta'
  | 'promoCodeInput_removeCta'
  | 'previewAllPostcards'
  | 'stickyCta_nextButton_withValidation'
  | 'StudioImage_applyToCard'
  | 'submitChangesCta';

export interface UnableToCheckoutErrorArgs extends Record<string, unknown> {
  action?: 'DigitalCardCheckout' | 'InternationalDigitalCardCheckout';
  name: string;
  error?: unknown;
}

const cardTelemetry = createTelemetryObject({
  pages: {
    customize: () => ({
      category: CATEGORY,
      pagePrefix: PAGE_PREFIX,
      page: PAGE,
    }),
    premiumDigitalEmailEditorDialog: () => ({
      category: CATEGORY,
      pagePrefix: PAGE_PREFIX,
      page: 'premiumDigitalEmailEditorDialog',
    }),
  },
  actions: {
    /**
     * Generic escape hatch for one-off events: supplies the `category`/`pagePrefix`/`page`
     * boilerplate so a distinct action needs no dedicated named method.
     *
     * Only for events with no `actionType` — that field renames the event on the wire
     * (`'SmartyApi'` + `'error'` → `SmartyApiError`), so those events keep a named method.
     *
     * Prefer a named method when an event has multiple call sites (the method enforces payload
     * consistency across callers) or when the payload needs a shared computation.
     *
     * On the deliberate loss of payload typing: the named methods this replaced typed each
     * payload (`AddressType`, `LayerType`, `'low' | 'high'`, …); `extraInfo` here is
     * `Record<string, unknown>`, so payload keys and value shapes are unchecked. That is an
     * accepted trade, not an oversight. Event *names* — the thing dashboards actually key on,
     * and the thing a typo silently breaks — are now union-typed and `findReferences`-able,
     * which they were not before. Payload drift, by contrast, is caught in the analytics
     * pipeline rather than at compile time, and these payloads are written once at a single
     * call site and rarely touched again. A mapped name-to-payload type could recover both,
     * but it buys little for one-off events and is not worth the indirection here; reach for a
     * named method instead on any event where payload consistency genuinely matters.
     */
    track: (action: CardCustomizerTrackedAction, extraInfo?: Record<string, unknown>) => ({
      category: CATEGORY,
      pagePrefix: PAGE_PREFIX,
      page: PAGE,
      action,
      extraInfo,
    }),
    /**
     * `track` for error events: adds `actionType: 'error'`, which logs at error level and
     * appends `Error` to the event name on the wire. See {@link CardCustomizerTrackedErrorAction}.
     */
    trackError: (action: CardCustomizerTrackedErrorAction, extraInfo?: Record<string, unknown>) => ({
      category: CATEGORY,
      pagePrefix: PAGE_PREFIX,
      page: PAGE,
      action,
      actionType: 'error',
      extraInfo,
    }),
    /**
     * `track` for the shared `MessagingButtonInteracted` event, where `name` carries the
     * identity instead of `action`. See {@link CardCustomizerButtonName}.
     *
     * TODO: overlaps with `messagingButtonInteracted`, which emits the same event with a
     * free-form `name: string`. The two are to be merged in the next phase — this one is the
     * intended survivor, since the union keeps `name` typo-proof and `findReferences`-able.
     * Merging is deferred only because `messagingButtonInteracted` spans 11 files, past the
     * agreed migration threshold. Prefer this method for new button events.
     */
    buttonInteracted: (name: CardCustomizerButtonName, extraInfo?: Record<string, unknown>) => ({
      category: CATEGORY,
      pagePrefix: PAGE_PREFIX,
      page: PAGE,
      action: 'MessagingButtonInteracted',
      // `name` is spread last so an `extraInfo.name` can never clobber the union-typed identity.
      extraInfo: { ...extraInfo, name },
    }),
    /**
     * Also emits `MessagingButtonInteracted`, but maps `property`/`value` onto `name`/`value`
     * rather than taking a `name` directly — so it is a third shape for the same event. Out of
     * scope for the `buttonInteracted` merge noted above (20 call-site files), but worth folding
     * in if that consolidation ever goes further.
     */
    customizationButtonInteracted: (args: CustomizationButtonInteractedArgs) => ({
      category: CATEGORY,
      pagePrefix: PAGE_PREFIX,
      page: PAGE,
      action: 'MessagingButtonInteracted',
      extraInfo: {
        name: args.property,
        value: args.value,
      },
    }),
    unableToCheckoutError: (args: UnableToCheckoutErrorArgs) => ({
      category: CATEGORY,
      pagePrefix: PAGE_PREFIX,
      page: PAGE,
      action: args.action || 'CardCheckout',
      actionType: 'error',
      extraInfo: { ...args },
    }),

    /**
     * TODO: superseded by `buttonInteracted`, which emits this same event with `name` constrained
     * to {@link CardCustomizerButtonName}. To be merged into it in the next phase; kept for now
     * because its 11 call-site files exceed the agreed migration threshold. Don't add new callers —
     * add the `name` to the union and use `buttonInteracted` instead.
     */
    messagingButtonInteracted: (name: string) => ({
      category: CATEGORY,
      pagePrefix: PAGE_PREFIX,
      page: PAGE,
      action: 'MessagingButtonInteracted',
      extraInfo: {
        name,
      },
    }),
  },
});

export type EnrichedCardCustomizerExtraInfo = {
  stationeryDraftId: string | null;
  stationeryDraftFormat: StationeryDraftFormat | null;
  stationeryTemplateCategory: StationeryTemplateCategoryEnum | null;
  themeId: string | null;
  step: NavigationStep | null;
  pageOverride: Maybe<'pdpModal'>;
  orderNumber: Maybe<string>;
  isComingFromStripeCheckout?: boolean;
};

const {
  TelemetryProvider: CardCustomizerTelemetryProvider,
  useTelemetry: useCardCustomizerTelemetry,
  enrichTelemetryExtraInfo: enrichCardCutomizerTelemetryExtraInfo,
} = createTelemetry(cardTelemetry, undefined, undefined, {
  stationeryDraftId: null,
  stationeryTemplateCategory: null,
  themeId: null,
  step: null,
  orderNumber: null,
  stationeryDraftFormat: null,
} as EnrichedCardCustomizerExtraInfo);

export { CardCustomizerTelemetryProvider, useCardCustomizerTelemetry, enrichCardCutomizerTelemetryExtraInfo };
